// WS + ws-resume for admin internal position applications (mirrors structure-change requests)

import { useCallback, useEffect, useRef } from 'react';
import { orgPositionApplicationsSocket } from '../../utils/Socket';
import { resumeAll, registerSocket } from '../wsRecovery/wsResume';
import { setResumePointer, updateResumeSeq } from '../wsRecovery/wsResumeStore';
import { startHeartbeat, stopHeartbeat } from '../wsRecovery/heartbeat';
import {
  getOrgPositionApplicationsWsDiff,
  orgPositionApplicationsWsLastSeen,
  ORG_POSITION_APPLICATIONS_WS_CHANNEL,
  wsResumeOrgPositionApplications,
} from '../pages/private/admin/organisationMembersTabs/internalApplications/internalApplicationsApi';
import {
  fetchPendingInternalApplicationsCountOnce,
  invalidateInternalApplicationsList,
  setPendingInternalApplicationsCount,
} from './internalApplicationsPendingStore';

const RESUME_REGISTRY_KEY = 'orgPositionApplications';

let socketListenerCount = 0;
let resumeHandlerRegistered = false;

function applyPendingFromPayload(payload) {
  if (payload && typeof payload.pendingCount === 'number') {
    setPendingInternalApplicationsCount(payload.pendingCount);
    return true;
  }
  return false;
}

export default function useInternalApplicationsSocket({ enabled = true, isLoggedIn = true } = {}) {
  const lastResumeIdRef = useRef(0);

  const syncPointer = useCallback(async (wsLogId) => {
    const seq = Number(wsLogId || 0);
    if (seq > 0) {
      await orgPositionApplicationsWsLastSeen(seq).catch(() => {});
      lastResumeIdRef.current = Math.max(lastResumeIdRef.current, seq);
      updateResumeSeq(ORG_POSITION_APPLICATIONS_WS_CHANNEL, seq);
    }
  }, []);

  const handleWsPayload = useCallback(
    async (payload, { invalidateList = true } = {}) => {
      const wsLogId = Number(payload?.wsLogId || 0);
      if (wsLogId > 0) {
        lastResumeIdRef.current = Math.max(lastResumeIdRef.current, wsLogId);
      }

      if (!applyPendingFromPayload(payload)) {
        await fetchPendingInternalApplicationsCountOnce();
      }

      if (invalidateList) {
        invalidateInternalApplicationsList();
      }

      if (wsLogId > 0) {
        await syncPointer(wsLogId);
      }
    },
    [syncPointer],
  );

  const runWsResume = useCallback(async () => {
    try {
      const data = await wsResumeOrgPositionApplications(lastResumeIdRef.current);
      if (!data?.ok || !data.events?.length) return;

      const maxSeq = Math.max(...data.events.map((e) => Number(e.seq) || 0), 0);
      const lastEvent = data.events[data.events.length - 1];
      const pendingFromMeta = lastEvent?.meta?.pendingCount;

      if (typeof pendingFromMeta === 'number') {
        setPendingInternalApplicationsCount(pendingFromMeta);
      } else {
        await fetchPendingInternalApplicationsCountOnce();
      }

      invalidateInternalApplicationsList();

      if (maxSeq > 0) {
        lastResumeIdRef.current = Math.max(lastResumeIdRef.current, maxSeq);
        await syncPointer(maxSeq);
      }
    } catch (err) {
      console.error('[OrgPositionApplications] ws-resume failed:', err);
    }
  }, [syncPointer]);

  useEffect(() => {
    if (!enabled || !isLoggedIn) return undefined;

    setResumePointer(ORG_POSITION_APPLICATIONS_WS_CHANNEL, {});

    if (!resumeHandlerRegistered) {
      resumeHandlerRegistered = true;
      registerSocket(RESUME_REGISTRY_KEY, async () => {
        if (!orgPositionApplicationsSocket.connected) {
          orgPositionApplicationsSocket.connect();
          startHeartbeat(orgPositionApplicationsSocket);
        }
        await runWsResume();
        invalidateInternalApplicationsList();
      });
    }

    return undefined;
  }, [enabled, isLoggedIn, runWsResume]);

  useEffect(() => {
    if (!enabled || !isLoggedIn) return undefined;

    if (!orgPositionApplicationsSocket.connected) {
      orgPositionApplicationsSocket.connect();
      startHeartbeat(orgPositionApplicationsSocket);
    }

    const onConnect = () => {
      orgPositionApplicationsSocket.emit('joinOrgPositionApplications');
      resumeAll();
    };

    const onReconnect = async () => {
      orgPositionApplicationsSocket.emit('joinOrgPositionApplications');
      try {
        const diff = await getOrgPositionApplicationsWsDiff();
        if (diff?.needRefresh) {
          const lastEvent = Array.isArray(diff.events) ? diff.events[diff.events.length - 1] : null;
          if (lastEvent?.meta && typeof lastEvent.meta.pendingCount === 'number') {
            setPendingInternalApplicationsCount(lastEvent.meta.pendingCount);
          } else {
            await fetchPendingInternalApplicationsCountOnce();
          }
          invalidateInternalApplicationsList();
          if (diff.lastWsLogId) {
            await syncPointer(diff.lastWsLogId);
          }
        } else if (diff?.maxWsLogSeq) {
          lastResumeIdRef.current = Math.max(
            lastResumeIdRef.current,
            Number(diff.maxWsLogSeq),
          );
        }
      } catch (err) {
        console.error('[OrgPositionApplications] reconnect diff failed:', err);
      }
    };

    orgPositionApplicationsSocket.on('connect', onConnect);
    orgPositionApplicationsSocket.on('reconnect', onReconnect);

    return () => {
      stopHeartbeat(orgPositionApplicationsSocket);
      orgPositionApplicationsSocket.off('connect', onConnect);
      orgPositionApplicationsSocket.off('reconnect', onReconnect);
    };
  }, [enabled, isLoggedIn, syncPointer]);

  useEffect(() => {
    const handler = () => {
      if (!enabled || !isLoggedIn) return;
      if (!orgPositionApplicationsSocket.connected) {
        orgPositionApplicationsSocket.connect();
        startHeartbeat(orgPositionApplicationsSocket);
      }
    };
    window.addEventListener('auth-ws-refreshed', handler);
    return () => window.removeEventListener('auth-ws-refreshed', handler);
  }, [enabled, isLoggedIn]);

  useEffect(() => {
    if (!enabled || !isLoggedIn) return undefined;

    const resyncIfStale = async () => {
      try {
        const diff = await getOrgPositionApplicationsWsDiff();
        if (diff?.needRefresh) {
          const lastEvent = Array.isArray(diff.events) ? diff.events[diff.events.length - 1] : null;
          if (lastEvent?.meta && typeof lastEvent.meta.pendingCount === 'number') {
            setPendingInternalApplicationsCount(lastEvent.meta.pendingCount);
          } else {
            await fetchPendingInternalApplicationsCountOnce();
          }
          invalidateInternalApplicationsList();
        }
      } catch (err) {
        console.error('[OrgPositionApplications] visibility/online diff failed:', err);
      }
    };

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      resyncIfStale();
    };

    const onOnline = () => {
      resyncIfStale();
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
    };
  }, [enabled, isLoggedIn]);

  useEffect(() => {
    if (!enabled || !isLoggedIn) return undefined;

    socketListenerCount += 1;

    if (!orgPositionApplicationsSocket.connected) {
      orgPositionApplicationsSocket.connect();
      startHeartbeat(orgPositionApplicationsSocket);
    }

    orgPositionApplicationsSocket.emit('joinOrgPositionApplications');

    const onMissed = async (payload) => {
      try {
        await handleWsPayload(payload || {}, { invalidateList: true });
      } catch (err) {
        console.error('[OrgPositionApplications] missed handler failed:', err);
      }
    };

    const onUpdate = async (payload) => {
      try {
        await handleWsPayload(payload || {}, { invalidateList: true });
      } catch (err) {
        console.error('[OrgPositionApplications] update handler failed:', err);
      }
    };

    orgPositionApplicationsSocket.on('orgPositionApplicationsMissed', onMissed);
    orgPositionApplicationsSocket.on('orgPositionApplicationsUpdate', onUpdate);

    return () => {
      orgPositionApplicationsSocket.off('orgPositionApplicationsMissed', onMissed);
      orgPositionApplicationsSocket.off('orgPositionApplicationsUpdate', onUpdate);
      socketListenerCount -= 1;
      if (socketListenerCount === 0) {
        orgPositionApplicationsSocket.emit('leaveOrgPositionApplications');
      }
    };
  }, [enabled, isLoggedIn, handleWsPayload]);

  return { lastResumeIdRef };
}
